import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Zap, Workflow, Clock, Settings, ArrowRight, CheckCircle, 
  Play, Star, Calendar, Bot, GitBranch, Code, Bell, Target,
  Users, FileText, Repeat, Timer, ChevronRight, Cog, Shield
} from 'lucide-react';

const WorkflowAutomation = () => {
  const [activeWorkflow, setActiveWorkflow] = useState('process');

  const automationFeatures = [
    {
      id: 'process',
      name: 'Process Automation',
      icon: Workflow,
      description: 'Visual workflow designer for complex business processes',
      capabilities: [
        'Drag-and-drop workflow designer with intuitive interface',
        'Multi-stage approval processes with parallel paths',
        'Conditional logic and branching based on data',
        'Error handling and retry mechanisms for reliability',
        'Integration with external systems and APIs',
        'Real-time process monitoring and tracking'
      ],
      apiEndpoints: [
        { name: 'saveAutomaton', description: 'Create and update automation rules' },
        { name: 'getAllAutomatons', description: 'List all automation workflows' },
        { name: 'deleteAutomaton', description: 'Remove automation rules' }
      ],
      examples: [
        'Employee onboarding with document collection and approvals',
        'Purchase order approval with budget validation',
        'Customer support ticket escalation and routing',
        'Invoice processing with multi-level authorization'
      ]
    },
    {
      id: 'scheduled',
      name: 'Scheduled Jobs & Tasks',
      icon: Clock,
      description: 'Automated task scheduling with flexible timing options',
      capabilities: [
        'Cron-based scheduling for precise timing control',
        'Interval-based execution for recurring tasks',
        'Job monitoring with detailed execution logs',
        'Failure notifications and automatic retry logic',
        'Dependency management between related jobs',
        'Resource allocation and load balancing'
      ],
      apiEndpoints: [
        { name: 'getAllJobs', description: 'List all scheduled jobs' },
        { name: 'createScheduledJob', description: 'Create new scheduled jobs' },
        { name: 'deleteJob', description: 'Remove scheduled jobs' },
        { name: 'activateDeactivateJob', description: 'Toggle job status' }
      ],
      examples: [
        'Daily data backup and synchronization procedures',
        'Monthly financial report generation and distribution',
        'Weekly inventory reorder point calculations',
        'Hourly system health checks and maintenance'
      ]
    },
    {
      id: 'functions',
      name: 'Custom Functions & Triggers',
      icon: Code,
      description: 'Create custom business logic with event-driven triggers',
      capabilities: [
        'Visual function builder with drag-and-drop nodes',
        'Event-based triggers (onCreate, onUpdate, onDelete)',
        'Custom validation rules and business logic',
        'Integration with external services and APIs',
        'Variable management and data transformation',
        'Function compilation and performance optimization'
      ],
      apiEndpoints: [
        { name: 'getAllFunctions', description: 'List all custom functions' },
        { name: 'saveFunction', description: 'Create and update functions' },
        { name: 'deleteFunction', description: 'Remove custom functions' },
        { name: 'compileFunction', description: 'Compile and optimize functions' },
        { name: 'getFunctionFromPrompt', description: 'Generate functions from natural language' }
      ],
      examples: [
        'Automatic invoice generation when orders are completed',
        'Real-time inventory updates across multiple systems',
        'Customer notification systems for order status',
        'Lead scoring and assignment based on criteria'
      ]
    }
  ];

  const workflowTypes = [
    {
      category: 'Approval Workflows',
      icon: '✅',
      description: 'Multi-level approval processes',
      examples: ['Purchase Orders', 'Expense Reports', 'Time Off Requests', 'Budget Approvals']
    },
    {
      category: 'Data Processing',
      icon: '🔄',
      description: 'Automated data handling',
      examples: ['Data Import/Export', 'Record Updates', 'Data Validation', 'Synchronization']
    },
    {
      category: 'Notification Systems',
      icon: '🔔',
      description: 'Automated communications',
      examples: ['Email Campaigns', 'SMS Alerts', 'In-app Notifications', 'Report Distribution']
    },
    {
      category: 'Business Rules',
      icon: '📋',
      description: 'Custom business logic',
      examples: ['Pricing Rules', 'Inventory Management', 'Customer Segmentation', 'Quality Control']
    }
  ];

  const triggerTypes = [
    { name: 'onCreate', description: 'When new records are created', icon: '➕' },
    { name: 'onUpdate', description: 'When records are modified', icon: '✏️' },
    { name: 'onDelete', description: 'When records are removed', icon: '🗑️' },
    { name: 'onSchedule', description: 'At specified times or intervals', icon: '⏰' },
    { name: 'onCondition', description: 'When specific conditions are met', icon: '🎯' },
    { name: 'onEvent', description: 'Custom application events', icon: '⚡' }
  ];

  const automationBenefits = [
    {
      title: 'Increased Efficiency',
      description: 'Automate repetitive tasks and reduce manual work',
      icon: Zap,
      metrics: ['75% time savings', '90% error reduction', '24/7 operation']
    },
    {
      title: 'Improved Compliance',
      description: 'Ensure consistent processes and audit trails',
      icon: Shield,
      metrics: ['100% process adherence', 'Complete audit logs', 'Regulatory compliance']
    },
    {
      title: 'Better Scalability',
      description: 'Handle growing workloads without additional staff',
      icon: Target,
      metrics: ['10x capacity increase', 'Instant scaling', 'Cost optimization']
    },
    {
      title: 'Enhanced Visibility',
      description: 'Real-time monitoring and reporting of all processes',
      icon: Bell,
      metrics: ['Real-time tracking', 'Performance analytics', 'Proactive alerts']
    }
  ];

  const industryWorkflows = [
    {
      industry: 'Manufacturing',
      icon: '🏭',
      workflows: [
        'Quality control inspection processes',
        'Production scheduling and resource allocation',
        'Supplier approval and onboarding',
        'Maintenance request and completion tracking'
      ]
    },
    {
      industry: 'Healthcare',
      icon: '🏥',
      workflows: [
        'Patient admission and discharge procedures',
        'Medical equipment maintenance scheduling',
        'Insurance claim processing and approval',
        'Staff certification and training tracking'
      ]
    },
    {
      industry: 'Financial Services',
      icon: '🏦',
      workflows: [
        'Loan application and approval processes',
        'KYC and compliance verification',
        'Risk assessment and monitoring',
        'Regulatory reporting automation'
      ]
    },
    {
      industry: 'Retail',
      icon: '🛒',
      workflows: [
        'Inventory reordering and management',
        'Customer return and refund processing',
        'Vendor payment and invoice processing',
        'Promotional campaign execution'
      ]
    }
  ];

  const testimonials = [
    {
      quote: "Workflow automation reduced our invoice processing time from days to hours. The approval chains work flawlessly.",
      name: "",
      title: "",
      company: "Global Enterprises",
      avatar: "👨‍💼",
      metric: "85% faster processing"
    },
    {
      quote: "The scheduled jobs feature automated our entire reporting pipeline. No more manual work on monthly reports.",
      name: "",
      title: "",
      company: "DataFlow Inc",
      avatar: "👩‍💼",
      metric: "100% automated reporting"
    },
    {
      quote: "Custom functions allowed us to implement complex business rules without any coding. It's incredibly powerful.",
      name: "",
      title: "",
      company: "Process Innovations",
      avatar: "👨‍💻",
      metric: "50+ custom automations"
    }
  ];

  const stats = [
    { number: '10,000+', label: 'Workflows Active', description: 'Across all client systems' },
    { number: '99.9%', label: 'Automation Reliability', description: 'Successful execution rate' },
    { number: '24/7', label: 'Continuous Operation', description: 'Round-the-clock processing' },
    { number: '75%', label: 'Average Time Savings', description: 'Reduction in manual work' }
  ];

  return (
    <div className="min-h-screen bg-white">
                {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-yellow-900 via-orange-900 to-red-900 overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center lg:text-left text-white"
            >
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-r from-yellow-500 to-orange-500 text-white mb-6">
              <Zap className="h-10 w-10" />
            </div>
            <h1 className="text-5xl lg:text-6xl font-bold mb-6">
              Workflow & Automation
              <span className="block text-3xl lg:text-4xl font-normal mt-2 text-yellow-200">
                Intelligent Process Automation
              </span>
            </h1>
            <p className="text-xl lg:text-2xl text-white/80 mb-10 leading-relaxed">
              Streamline your business operations with powerful automation tools that handle 
              everything from simple tasks to complex multi-stage workflows.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.a
                href="https://app.aquargin.com/register"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="bg-white text-gray-900 px-8 py-4 rounded-lg font-semibold text-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center group"
              >
                Try Automation Free
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </motion.a>
              
              <motion.a
                href="https://www.youtube.com/@nocodeAquargin"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white hover:text-gray-900 transition-colors duration-300 flex items-center justify-center group"
              >
                <Play className="mr-2 h-5 w-5" />
                Watch Workflow Demo
              </motion.a>
            </div>
          </motion.div>
          
          {/* Workflow Visualization */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="relative"
          >
            <img
              src="/images/Split_composition_showing_mechanical_clockwork_gea_1750595663.png"
              alt="Workflow Automation Transformation"
              className="w-full h-auto rounded-2xl shadow-2xl"
            />
          </motion.div>
        </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center bg-white p-6 rounded-xl shadow-lg"
              >
                <div className="text-4xl font-bold text-orange-600 mb-2">{stat.number}</div>
                <div className="text-gray-900 font-semibold mb-1">{stat.label}</div>
                <div className="text-sm text-gray-600">{stat.description}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Workflow Features Showcase */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="text-center">
              <img
                src="/images/Visual_workflow_builder_with_drag-and-drop_interfa_1750595634.png"
                alt="Workflow Designer Interface"
                className="w-full h-48 object-cover rounded-lg shadow-lg mb-4"
              />
              <h3 className="text-lg font-semibold text-gray-900">Visual Workflow Designer</h3>
              <p className="text-gray-600 mt-2">Drag-and-drop interface for building complex processes</p>
            </div>
            <div className="text-center">
              <img
                src="/images/Visual_representation_of_automated_triggers_and_co_1750596720.png"
                alt="Automation Triggers"
                className="w-full h-48 object-cover rounded-lg shadow-lg mb-4"
              />
              <h3 className="text-lg font-semibold text-gray-900">Smart Triggers</h3>
              <p className="text-gray-600 mt-2">Event-driven automation with intelligent conditions</p>
            </div>
            <div className="text-center">
              <img
                src="/images/Automation-board.png"
                alt="Automation Dashboard"
                className="w-full h-48 object-cover rounded-lg shadow-lg mb-4"
              />
              <h3 className="text-lg font-semibold text-gray-900">Automation Board</h3>
              <p className="text-gray-600 mt-2">Monitor and manage all your automated processes</p>
            </div>
          </div>
        </div>
      </section>

      {/* Core Automation Features */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Automation Capabilities
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive automation tools for every business process
            </p>
          </div>

          {/* Feature Tabs */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {automationFeatures.map((feature) => (
              <button
                key={feature.id}
                onClick={() => setActiveWorkflow(feature.id)}
                className={`flex items-center space-x-3 px-6 py-3 rounded-lg font-semibold transition-all duration-300 ${
                  activeWorkflow === feature.id
                    ? 'bg-gradient-to-r from-yellow-500 to-orange-500 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <feature.icon className="h-5 w-5" />
                <span>{feature.name}</span>
              </button>
            ))}
          </div>

          {/* Active Feature Content */}
          {automationFeatures.map((feature) => (
            activeWorkflow === feature.id && (
              <motion.div
                key={feature.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="max-w-6xl mx-auto"
              >
                <div className="grid lg:grid-cols-2 gap-12">
                  <div>
                    <div className="flex items-center mb-6">
                      <div className="w-12 h-12 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-lg flex items-center justify-center mr-4">
                        <feature.icon className="h-6 w-6 text-white" />
                      </div>
                      <h3 className="text-3xl font-bold text-gray-900">{feature.name}</h3>
                    </div>
                    <p className="text-xl text-gray-600 mb-8">{feature.description}</p>
                    
                    <div className="space-y-4">
                      <h4 className="text-lg font-semibold text-gray-900 flex items-center">
                        <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                        Key Features
                      </h4>
                      <ul className="space-y-3">
                        {feature.capabilities.map((capability, idx) => (
                          <li key={idx} className="flex items-start space-x-3">
                            <div className="w-2 h-2 bg-orange-500 rounded-full mt-2 flex-shrink-0"></div>
                            <span className="text-gray-700">{capability}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <div className="space-y-8">
                    <div className="bg-gray-50 p-6 rounded-2xl">
                      <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <Code className="h-5 w-5 text-orange-500 mr-2" />
                        API Endpoints
                      </h4>
                      <div className="space-y-3">
                        {feature.apiEndpoints.map((endpoint, idx) => (
                          <div key={idx} className="bg-white p-3 rounded-lg border-l-4 border-orange-500">
                            <div className="font-mono text-sm text-orange-600 font-semibold">
                              {endpoint.name}
                            </div>
                            <div className="text-sm text-gray-600 mt-1">
                              {endpoint.description}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="bg-orange-50 p-6 rounded-2xl">
                      <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <Target className="h-5 w-5 text-orange-500 mr-2" />
                        Example Automations
                      </h4>
                      <div className="space-y-3">
                        {feature.examples.map((example, idx) => (
                          <div key={idx} className="flex items-start space-x-3">
                            <ChevronRight className="h-5 w-5 text-orange-500 mt-0.5 flex-shrink-0" />
                            <p className="text-gray-700">{example}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            )
          ))}
        </div>
      </section>

      {/* Workflow Types */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Workflow Categories
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Pre-built workflow templates for common business processes
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {workflowTypes.map((workflow, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow"
              >
                <div className="flex items-center mb-6">
                  <div className="text-4xl mr-4">{workflow.icon}</div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">{workflow.category}</h3>
                    <p className="text-gray-600">{workflow.description}</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  {workflow.examples.map((example, idx) => (
                    <div key={idx} className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                      <span className="text-sm text-gray-700">{example}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Trigger Types */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Automation Triggers
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Multiple ways to trigger your automated workflows
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
            {triggerTypes.map((trigger, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="bg-gray-50 p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow text-center"
              >
                <div className="text-4xl mb-3">{trigger.icon}</div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{trigger.name}</h3>
                <p className="text-sm text-gray-600">{trigger.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Automation Benefits */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Automation Benefits
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Transform your business with intelligent automation
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {automationBenefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow"
              >
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-lg flex items-center justify-center mr-4">
                    <benefit.icon className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">{benefit.title}</h3>
                    <p className="text-gray-600">{benefit.description}</p>
                  </div>
                </div>
                
                <div className="flex flex-wrap gap-3">
                  {benefit.metrics.map((metric, idx) => (
                    <span key={idx} className="bg-orange-100 text-orange-700 px-3 py-1 rounded-full text-sm font-semibold">
                      {metric}
                    </span>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Industry Workflows */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Industry-Specific Workflows
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Automation solutions tailored for different industries
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {industryWorkflows.map((industry, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-gray-50 p-8 rounded-2xl hover:shadow-lg transition-shadow"
              >
                <div className="flex items-center mb-6">
                  <div className="text-4xl mr-4">{industry.icon}</div>
                  <h3 className="text-2xl font-bold text-gray-900">{industry.industry}</h3>
                </div>
                
                <div className="space-y-3">
                  {industry.workflows.map((workflow, idx) => (
                    <div key={idx} className="flex items-start space-x-3">
                      <Workflow className="h-5 w-5 text-orange-500 mt-0.5 flex-shrink-0" />
                      <p className="text-gray-700">{workflow}</p>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Automation Success Stories
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              See how organizations transformed their operations with automation
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow"
              >
                <div className="flex items-center mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                
                <blockquote className="text-gray-700 mb-6 text-lg leading-relaxed">
                  "{testimonial.quote}"
                </blockquote>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="text-3xl mr-3">{testimonial.avatar}</div>
                    <div>
                      <div className="font-bold text-gray-900">{testimonial.name}</div>
                      <div className="text-sm text-gray-600">{testimonial.title}</div>
                      <div className="text-sm text-gray-500">{testimonial.company}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-orange-600">{testimonial.metric}</div>
                    <div className="text-xs text-gray-500">Result</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-yellow-600 to-orange-600 relative overflow-hidden">
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-4xl mx-auto text-white"
          >
            <h2 className="text-4xl lg:text-5xl font-bold mb-6">
              Automate Your Business Today
            </h2>
            <p className="text-xl mb-10 opacity-90">
              Start your free trial and discover how workflow automation 
              can transform your business operations.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.a
                href="https://app.aquargin.com/register"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-white text-gray-900 px-8 py-4 rounded-lg font-bold text-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center group"
              >
                Start Automation Trial
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </motion.a>
              
              <motion.a
                href="/contact"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="border-2 border-white text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-white hover:text-gray-900 transition-colors duration-300 flex items-center justify-center group"
              >
                <Workflow className="mr-2 h-5 w-5" />
                Schedule Workflow Demo
              </motion.a>
            </div>

            <div className="mt-8 text-white/80 text-sm">
              <p>✓ No credit card required  ✓ Full automation access  ✓ 24/7 support included</p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default WorkflowAutomation; 